import { createContext, useContext, useCallback, type ReactNode } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import type { RouteInfo, RouteDaySpot } from '@/mocks/routeData';

export interface TripConfig {
  days: string;
  crowd: string;
  preferences: string[];
}

export interface ShareData {
  routeName: string;
  days: number;
  completedSpots: number;
  totalSpots: number;
  visitedSpotNames: string[];
  feeling: string;
}

interface TripContextType {
  selectedSpots: string[];
  spotStatus: Record<string, string>;
  routeConfig: TripConfig;
  currentRoute: RouteInfo | null;
  addSpot: (id: string) => void;
  removeSpot: (id: string) => void;
  toggleSpot: (id: string) => boolean;
  isInTrip: (id: string) => boolean;
  setRouteConfig: (config: Partial<TripConfig>) => void;
  setCurrentRoute: (route: RouteInfo | null) => void;
  startRoute: (route: RouteInfo, config: TripConfig) => void;
  updateSpotStatus: (id: string, status: string) => void;
  checkInSpot: (id: string) => void;
  getSpotStatus: (spotId: string, defaultStatus?: string) => string;
  getCompletedCount: () => number;
  getNextSpot: () => RouteDaySpot | null;
  getShareData: () => ShareData;
  resetTrip: () => void;
}

const defaultConfig: TripConfig = {
  days: '三天两夜',
  crowd: '亲子',
  preferences: ['拍照'],
};

const TripContext = createContext<TripContextType | null>(null);

export function TripProvider({ children }: { children: ReactNode }) {
  // 初始状态全部为空 —— 新用户进来没有任何行程
  const [selectedSpots, setSelectedSpots] = useLocalStorage<string[]>('waj-trips-spots', []);
  const [spotStatus, setSpotStatus] = useLocalStorage<Record<string, string>>('waj-trips-status', {});
  const [routeConfig, setRouteConfigState] = useLocalStorage<TripConfig>('waj-trips-config', defaultConfig);
  const [currentRoute, setCurrentRouteState] = useLocalStorage<RouteInfo | null>('waj-current-route', null);

  const addSpot = useCallback((id: string) => {
    setSelectedSpots((prev) => {
      if (prev.includes(id)) return prev;
      return [...prev, id];
    });
    setSpotStatus((prev) => {
      if (prev[id]) return prev;
      return { ...prev, [id]: 'not-started' };
    });
  }, [setSelectedSpots, setSpotStatus]);

  const removeSpot = useCallback((id: string) => {
    setSelectedSpots((prev) => prev.filter((s) => s !== id));
    setSpotStatus((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, [setSelectedSpots, setSpotStatus]);

  const toggleSpot = useCallback((id: string) => {
    let result = false;
    setSelectedSpots((prev) => {
      if (prev.includes(id)) {
        return prev;
      }
      result = true;
      return [...prev, id];
    });
    setSpotStatus((prev) => {
      if (prev[id]) return prev;
      return { ...prev, [id]: 'not-started' };
    });
    return result;
  }, [setSelectedSpots, setSpotStatus]);

  const isInTrip = useCallback((id: string) => {
    return selectedSpots.includes(id);
  }, [selectedSpots]);

  const setRouteConfig = useCallback((config: Partial<TripConfig>) => {
    setRouteConfigState((prev) => ({ ...prev, ...config }));
  }, [setRouteConfigState]);

  const setCurrentRoute = useCallback((route: RouteInfo | null) => {
    setCurrentRouteState(route);
  }, [setCurrentRouteState]);

  /**
   * 开始一条新路线
   * 1. 设置路线和配置
   * 2. 初始化 spotStatus：第一个真实点位 in-progress，其他真实点位 not-started
   * 3. 建议节点不进入 spotStatus
   * 4. 将路线中所有真实点位的 placeId 加入 selectedSpots（避免遗漏）
   */
  const startRoute = useCallback((route: RouteInfo, config: TripConfig) => {
    setCurrentRouteState(route);
    setRouteConfigState(config);

    // 收集路线中所有真实点位
    const allReal = route.daysPlan
      .flatMap((d) => d.spots)
      .filter((s) => s.isRealPlace);

    // 初始化 spotStatus
    const newStatus: Record<string, string> = {};
    let firstSpotSet = false;

    for (const spot of allReal) {
      if (!firstSpotSet) {
        newStatus[spot.id] = 'in-progress';
        firstSpotSet = true;
      } else {
        newStatus[spot.id] = 'not-started';
      }
    }

    setSpotStatus(newStatus);

    // 同时把路线中所有真实点位的 placeId 加入 selectedSpots
    const routePlaceIds = allReal
      .map((s) => s.placeId)
      .filter((pid): pid is string => !!pid);

    setSelectedSpots((prev) => {
      const merged = new Set([...prev, ...routePlaceIds]);
      return Array.from(merged);
    });
  }, [setCurrentRouteState, setRouteConfigState, setSpotStatus, setSelectedSpots]);

  const updateSpotStatus = useCallback((id: string, status: string) => {
    setSpotStatus((prev) => ({ ...prev, [id]: status }));
  }, [setSpotStatus]);

  /** 打卡完成 + 自动推进下一个真实点位为 in-progress */
  const checkInSpot = useCallback((id: string) => {
    setSpotStatus((prev) => {
      const next = { ...prev, [id]: 'completed' };

      if (currentRoute) {
        const allReal = currentRoute.daysPlan
          .flatMap((d) => d.spots)
          .filter((s) => s.isRealPlace);

        // 清理所有其他 in-progress，只保留当前完成的那个
        for (const s of allReal) {
          if (s.id !== id && next[s.id] === 'in-progress') {
            next[s.id] = 'not-started';
          }
        }

        const idx = allReal.findIndex((s) => s.id === id);
        if (idx !== -1) {
          // 找到当前 spot 之后第一个不是 completed 的真实点位
          const following = allReal.slice(idx + 1);
          const nextSpot = following.find((s) => next[s.id] !== 'completed');
          if (nextSpot) {
            next[nextSpot.id] = 'in-progress';
          }
        }
      }
      return next;
    });
  }, [setSpotStatus, currentRoute]);

  const getSpotStatus = useCallback((spotId: string, defaultStatus = 'not-started') => {
    return spotStatus[spotId] || defaultStatus;
  }, [spotStatus]);

  const getCompletedCount = useCallback(() => {
    if (!currentRoute) return 0;
    const allReal = currentRoute.daysPlan
      .flatMap((d) => d.spots)
      .filter((s) => s.isRealPlace);
    return allReal.filter((s) => (spotStatus[s.id] || s.status) === 'completed').length;
  }, [spotStatus, currentRoute]);

  /** 只返回真实点位；优先 in-progress，其次第一个 not-started */
  const getNextSpot = useCallback(() => {
    if (!currentRoute) return null;
    const allReal = currentRoute.daysPlan
      .flatMap((d) => d.spots)
      .filter((s) => s.isRealPlace);

    const inProgress = allReal.find((s) => spotStatus[s.id] === 'in-progress');
    if (inProgress) return inProgress;

    return (
      allReal.find((s) => {
        const st = spotStatus[s.id] || s.status;
        return st !== 'completed';
      }) || null
    );
  }, [currentRoute, spotStatus]);

  const getShareData = useCallback(() => {
    if (!currentRoute) {
      return {
        routeName: '安吉行程',
        days: 1,
        completedSpots: 0,
        totalSpots: 0,
        visitedSpotNames: [],
        feeling: '准备出发，生成同款路线',
      };
    }

    const allReal = currentRoute.daysPlan
      .flatMap((d) => d.spots)
      .filter((s) => s.isRealPlace);

    const completed = allReal.filter((s) => (spotStatus[s.id] || s.status) === 'completed');
    const completedCount = completed.length;
    const totalSpots = allReal.length;

    let title: string;
    let subtitle: string;

    if (completedCount === 0) {
      title = '我的安吉之旅';
      subtitle = '准备出发，生成同款路线';
    } else if (completedCount >= totalSpots && totalSpots > 0) {
      title = `我在安吉玩了${currentRoute.days}天`;
      subtitle = `完成 ${completedCount} 个地方`;
    } else {
      title = `我在安吉打卡了${completedCount}个地方`;
      subtitle = '行程继续中';
    }

    return {
      routeName: title,
      days: currentRoute.days,
      completedSpots: completedCount,
      totalSpots,
      visitedSpotNames: completed.map((s) => s.name),
      feeling: subtitle,
    };
  }, [spotStatus, currentRoute]);

  const resetTrip = useCallback(() => {
    setSelectedSpots([]);
    setSpotStatus({});
    setRouteConfigState(defaultConfig);
    setCurrentRouteState(null);
  }, [setSelectedSpots, setSpotStatus, setRouteConfigState, setCurrentRouteState]);

  return (
    <TripContext.Provider
      value={{
        selectedSpots,
        spotStatus,
        routeConfig,
        currentRoute,
        addSpot,
        removeSpot,
        toggleSpot,
        isInTrip,
        setRouteConfig,
        setCurrentRoute,
        startRoute,
        updateSpotStatus,
        checkInSpot,
        getSpotStatus,
        getCompletedCount,
        getNextSpot,
        getShareData,
        resetTrip,
      }}
    >
      {children}
    </TripContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTrip() {
  const ctx = useContext(TripContext);
  if (!ctx) throw new Error('useTrip must be used within TripProvider');
  return ctx;
}
